import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Printer, ScanLine, Tablet, Package, Trash2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { id: "thermal_printer", label: "Thermal-printer", icon: Printer },
  { id: "barcode_scanner", label: "QR/Barcode-scanner", icon: ScanLine },
  { id: "labels", label: "Labels & ruller", icon: Package },
  { id: "tablet", label: "Tablet", icon: Tablet },
  { id: "stand", label: "Stander", icon: Package },
  { id: "signage", label: "Skiltning", icon: Package },
];

export default function EquipmentAdminTab({ token, partners = [] }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({
    partner_id: "", type: "thermal_printer", model_name: "", serial_number: "", value_dkk: 0, notes: "",
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pickup-equipment`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data.equipment || []);
    } finally { setLoading(false); }
  }, [token]);

  React.useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.partner_id) { toast.error("Vælg en partner"); return; }
    try {
      await axios.post(`${API}/admin/pickup-equipment`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Udstyr tildelt");
      setShowForm(false);
      setForm({ partner_id: "", type: "thermal_printer", model_name: "", serial_number: "", value_dkk: 0, notes: "" });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
  };

  const updateStatus = async (eq, status) => {
    let damage_charge_dkk = null;
    if (status === "damaged" || status === "lost") {
      const val = prompt(`Erstatningskrav i kr (forslag: ${eq.value_dkk}):`, eq.value_dkk);
      if (val === null) return;
      damage_charge_dkk = Number(val);
    }
    try {
      await axios.patch(`${API}/admin/pickup-equipment/${eq.id}`,
        { status, damage_charge_dkk },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Opdateret");
      await load();
    } catch (e) { toast.error("Fejl"); }
  };

  const del = async (eq) => {
    if (!confirm(`Slet udstyr "${eq.model_name || eq.type}"?`)) return;
    try {
      await axios.delete(`${API}/admin/pickup-equipment/${eq.id}`, { headers: { Authorization: `Bearer ${token}` } });
      await load();
    } catch (e) { toast.error("Fejl"); }
  };

  return (
    <div className="space-y-5" data-testid="equipment-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-brand-text2">Tildel og spor printere, scannere, labels og andet udstyr til pickup-point partnere.</p>
        <button onClick={() => setShowForm((s) => !s)} className="btn-primary text-sm" data-testid="eq-admin-add">
          <Plus size={14} /> Tildel udstyr
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-brand p-5 space-y-3" data-testid="eq-admin-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="label-flat">Partner *</label>
              <select className="input-flat" value={form.partner_id} onChange={(e) => setForm((f) => ({ ...f, partner_id: e.target.value }))} required>
                <option value="">— vælg —</option>
                {partners.filter(p => p.active).map((p) => <option key={p.id} value={p.id}>{p.shop_name} ({p.shop_city})</option>)}
              </select>
            </div>
            <div>
              <label className="label-flat">Type *</label>
              <select className="input-flat" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-flat">Model</label>
              <input className="input-flat" value={form.model_name} onChange={(e) => setForm((f) => ({ ...f, model_name: e.target.value }))} placeholder="fx Star TSP100" />
            </div>
            <div>
              <label className="label-flat">Serienummer</label>
              <input className="input-flat font-mono" value={form.serial_number} onChange={(e) => setForm((f) => ({ ...f, serial_number: e.target.value }))} />
            </div>
            <div>
              <label className="label-flat">Værdi (kr)</label>
              <input className="input-flat" type="number" min={0} value={form.value_dkk} onChange={(e) => setForm((f) => ({ ...f, value_dkk: Number(e.target.value) }))} />
            </div>
            <div className="sm:col-span-2">
              <label className="label-flat">Bemærkning</label>
              <input className="input-flat" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <button type="submit" className="btn-primary">Tildel udstyret</button>
        </form>
      )}

      {loading && <div className="text-brand-text2 text-center py-6">Indlæser...</div>}
      {!loading && (
        <div className="bg-white border border-brand">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg text-brand-text2 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Shop</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Model / SN</th>
                <th className="text-right p-3">Værdi</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Handling</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-brand-text2">Endnu ingen tildelt udstyr</td></tr>
              )}
              {items.map((eq) => (
                <tr key={eq.id} className="border-t border-brand" data-testid={`eq-row-${eq.id}`}>
                  <td className="p-3">{eq.shop_name}</td>
                  <td className="p-3">{TYPES.find(t => t.id === eq.type)?.label || eq.type}</td>
                  <td className="p-3">
                    <div>{eq.model_name || "—"}</div>
                    {eq.serial_number && <div className="text-xs text-brand-text2 font-mono">SN: {eq.serial_number}</div>}
                  </td>
                  <td className="p-3 text-right">{eq.value_dkk?.toFixed?.(0) || 0} kr</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      eq.status === "assigned" ? "bg-emerald-50 text-emerald-700" :
                      eq.status === "returned" ? "bg-slate-100 text-slate-700" :
                      eq.status === "damaged" ? "bg-amber-100 text-amber-800" :
                      "bg-red-100 text-red-800"
                    }`}>{eq.status}</span>
                    {eq.damage_charge_dkk > 0 && <div className="text-xs text-red-700 mt-1">{eq.damage_charge_dkk} kr</div>}
                  </td>
                  <td className="p-3 text-right">
                    {eq.status === "assigned" && (
                      <div className="flex gap-1 justify-end flex-wrap">
                        <button onClick={() => updateStatus(eq, "returned")} className="text-xs px-2 py-1 border border-brand hover:bg-brand-bg" title="Marker som returneret">Retur</button>
                        <button onClick={() => updateStatus(eq, "damaged")} className="text-xs px-2 py-1 border border-amber-300 text-amber-800 hover:bg-amber-50" title="Marker beskadiget + opret erstatningskrav">Beskadiget</button>
                        <button onClick={() => updateStatus(eq, "lost")} className="text-xs px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50" title="Marker tabt + opret erstatningskrav">Tabt</button>
                      </div>
                    )}
                    <button onClick={() => del(eq)} className="text-xs text-red-500 hover:text-red-700 ml-1" data-testid={`eq-delete-${eq.id}`}><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
